// Fake data for the /demo list-system mockups. Mirrors the shapes on the real
// screens (site plots, pot breakdown, activity) so the variants read true.

export type WorkStatus =
  | 'InProgress'
  | 'Booked'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled';

/**
 * Stands in for the org's configured variation work type display name. Real
 * orgs configure their own list ("Day work", "Plastering", …), which is why
 * variation rows take the name as a prop.
 */
export const DEMO_WORK_TYPE = 'Day work';

export interface Booking {
  ref: string;
  status: WorkStatus;
  bookedBy: string;
  bookedAt: string;
  amount: number;
  approvedBy?: string;
  approvedAt?: string;
}

export interface LabourItem {
  name: string;
  target: number;
  bookings: Booking[];
}

export interface Variation {
  ref: string;
  description: string;
  status: WorkStatus;
  bookedBy: string;
  bookedAt: string;
  amount: number;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

export interface Plot {
  number: string;
  gang: string | null;
  labourItems: LabourItem[];
  variations: Variation[];
}

export interface BookedRow {
  kind: 'labour' | 'variation' | 'deduction';
  title: string;
  ref: string;
  status: WorkStatus;
  bookedBy: string;
  bookedAt: string;
  amount: number;
  fullAmount?: number;
  site?: string;
  plot?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

export function itemBooked(item: LabourItem): number {
  return item.bookings
    .filter(b => b.status !== 'Cancelled' && b.status !== 'Rejected')
    .reduce((sum, b) => sum + b.amount, 0);
}

export function plotTotal(plot: Plot): number {
  return (
    plot.labourItems.reduce((sum, i) => sum + itemBooked(i), 0) +
    plot.variations
      .filter(v => v.status !== 'Cancelled' && v.status !== 'Rejected')
      .reduce((sum, v) => sum + v.amount, 0)
  );
}

export function plotTarget(plot: Plot): number {
  return plot.labourItems.reduce((sum, i) => sum + i.target, 0);
}

export function plotProgress(plot: Plot): number {
  const target = plot.labourItems.reduce((sum, i) => sum + i.target, 0);
  if (target === 0) return 0;
  const booked = plot.labourItems.reduce((sum, i) => sum + itemBooked(i), 0);
  return Math.round((booked / target) * 100);
}

// ── Site view ────────────────────────────────────────────────────────────────

export const SITE = {
  name: 'Kiddlington TF01',
  plots: [
    {
      number: '043',
      gang: 'Beckett',
      labourItems: [
        {
          name: 'Timber Frame Kit',
          target: 2095,
          bookings: [
            {
              ref: 'LAB-KID1-1',
              status: 'Approved',
              bookedBy: 'Sam Ashworth',
              bookedAt: '2026-06-24T09:12:00',
              amount: 2095,
              approvedBy: 'Natalia Kujawa',
              approvedAt: '2026-06-26T10:05:00',
            },
          ],
        },
      ],
      variations: [],
    },
    {
      number: '044',
      gang: 'Cooper',
      labourItems: [
        {
          name: 'Timber Frame Kit',
          target: 1780,
          bookings: [
            {
              ref: 'LAB-KID1-2',
              status: 'Approved',
              bookedBy: 'Sam Ashworth',
              bookedAt: '2026-06-24T09:14:00',
              amount: 1780,
              approvedBy: 'Natalia Kujawa',
              approvedAt: '2026-06-26T10:06:00',
            },
          ],
        },
      ],
      variations: [
        {
          ref: 'VAR-KID1-5',
          description: 'Step in wall remediate',
          status: 'Booked',
          bookedBy: 'Scott Bell',
          bookedAt: '2026-07-07T14:02:00',
          amount: 25,
        },
        {
          ref: 'VAR-KID1-4',
          description: 'Cassette remediation',
          status: 'Booked',
          bookedBy: 'Scott Bell',
          bookedAt: '2026-07-07T14:05:00',
          amount: 75,
        },
      ],
    },
    {
      number: '045',
      gang: 'Atkins',
      labourItems: [
        {
          name: 'Timber Frame Kit',
          target: 1590,
          bookings: [
            {
              ref: 'LAB-KID1-6',
              status: 'Approved',
              bookedBy: 'Sam Ashworth',
              bookedAt: '2026-07-05T08:41:00',
              amount: 1590,
              approvedBy: 'Natalia Kujawa',
              approvedAt: '2026-07-06T09:12:00',
            },
          ],
        },
      ],
      variations: [
        {
          ref: 'VAR-KID1-3',
          description: 'Pick ups moved P43',
          status: 'Booked',
          bookedBy: 'Scott Bell',
          bookedAt: '2026-07-05T10:12:00',
          amount: 25,
        },
        {
          ref: 'VAR-KID1-2',
          description: 'Pick ups moved P45',
          status: 'Booked',
          bookedBy: 'Scott Bell',
          bookedAt: '2026-07-05T10:14:00',
          amount: 25,
        },
      ],
    },
    {
      number: '047',
      gang: 'Hamlett',
      labourItems: [
        {
          name: 'Timber Frame Kit',
          target: 1769,
          bookings: [
            {
              ref: 'LAB-KID1-9',
              status: 'Approved',
              bookedBy: 'Kieron and Travis',
              bookedAt: '2026-07-12T15:22:00',
              amount: 469,
              approvedBy: 'Natalia Kujawa',
              approvedAt: '2026-07-13T08:30:00',
            },
            {
              ref: 'LAB-KID1-12',
              status: 'Booked',
              bookedBy: 'Kieron and Travis',
              bookedAt: '2026-07-18T14:19:00',
              amount: 650,
            },
          ],
        },
        { name: 'Roof Trusses', target: 840, bookings: [] },
      ],
      variations: [
        {
          ref: 'VAR-KID1-7',
          description: 'Door heights adjust - 8hrs',
          status: 'Rejected',
          bookedBy: 'Kieron and Travis',
          bookedAt: '2026-07-17T13:49:00',
          amount: 240,
          rejectedBy: 'Natalia Kujawa',
          rejectionReason:
            'Covered by the original kit rate, raise with the site manager if the doors were re-cut.',
        },
      ],
    },
    {
      number: '048',
      gang: null,
      labourItems: [
        { name: 'Timber Frame Kit', target: 1780, bookings: [] },
        { name: 'Roof Trusses', target: 840, bookings: [] },
        { name: 'First Fix Joinery', target: 1615, bookings: [] },
      ],
      variations: [],
    },
  ] as Plot[],
};

// ── Pot breakdown ────────────────────────────────────────────────────────────

export interface PotSite {
  name: string;
  plots: { number: string; rows: BookedRow[] }[];
}

export const POT_ORG_DEDUCTIONS: BookedRow[] = [
  {
    kind: 'deduction',
    title: 'Weekly advance repayment',
    ref: 'DED-ORG-3',
    status: 'Approved',
    bookedBy: 'Natalia Kujawa',
    bookedAt: '2026-07-20T09:00:00',
    amount: -150,
  },
];

export const POT = {
  owner: 'Romas Kvedys',
  week: 'Wk 30',
  sites: [
    {
      name: 'Stortford Fields',
      plots: [
        {
          number: '1',
          rows: [
            {
              kind: 'labour',
              title: 'Timber Frame Kit',
              ref: 'LAB-STOR-3',
              status: 'Booked',
              bookedBy: 'Kieron and Travis',
              bookedAt: '2026-07-18T14:18:00',
              amount: 1720,
              fullAmount: 1890,
            },
            {
              kind: 'variation',
              title: 'Ladders all broken',
              ref: 'VAR-STOR-2',
              status: 'Rejected',
              bookedBy: 'Romas Kvedys',
              bookedAt: '2026-07-20T22:57:00',
              amount: 600,
              rejectedBy: 'Natalia Kujawa',
              rejectionReason:
                'No day work agreed for this, ladders are supplied by the site.',
            },
            {
              kind: 'variation',
              title: 'Helping out',
              ref: 'VAR-STOR-3',
              status: 'Cancelled',
              bookedBy: 'Romas Kvedys',
              bookedAt: '2026-07-20T23:09:00',
              amount: 0,
            },
          ],
        },
      ],
    },
    {
      name: 'Kiddlington - TF',
      plots: [
        {
          number: '144',
          rows: [
            {
              kind: 'labour',
              title: 'Timber Frame Kit',
              ref: 'LAB-KIDD-129',
              status: 'Booked',
              bookedBy: 'Kieron and Travis',
              bookedAt: '2026-07-18T14:19:00',
              amount: 1769,
              fullAmount: 1890,
            },
            {
              kind: 'deduction',
              title: 'Cranes',
              ref: 'DED-KIDD-12',
              status: 'Approved',
              bookedBy: 'Natalia Kujawa',
              bookedAt: '2026-06-29T11:03:00',
              amount: -650,
              approvedBy: 'Natalia Kujawa',
              approvedAt: '2026-06-29T11:03:00',
            },
          ],
        },
      ],
    },
  ] as PotSite[],
};

// ── Activity ─────────────────────────────────────────────────────────────────

export interface ActivityWeek {
  label: string;
  rows: BookedRow[];
}

export const ACTIVITY: ActivityWeek[] = [
  {
    label: 'Week 30 · 20-26 Jul 26',
    rows: [
      {
        kind: 'variation',
        title: 'Helping out',
        ref: 'VAR-STOR-3',
        status: 'Cancelled',
        bookedBy: 'Romas Kvedys',
        bookedAt: '2026-07-20T23:09:00',
        amount: 600,
        site: 'Stortford Fields',
        plot: '1',
      },
      {
        kind: 'variation',
        title: 'Ladders all broken',
        ref: 'VAR-STOR-2',
        status: 'Rejected',
        bookedBy: 'Romas Kvedys',
        bookedAt: '2026-07-20T22:57:00',
        amount: 600,
        rejectedBy: 'Natalia Kujawa',
        rejectionReason:
          'No day work agreed for this, ladders are supplied by the site.',
        site: 'Stortford Fields',
        plot: '1',
      },
    ],
  },
  {
    label: 'Week 29 · 13-19 Jul 26',
    rows: [
      {
        kind: 'variation',
        title: 'Fixing windows',
        ref: 'VAR-WYNY-2',
        status: 'Booked',
        bookedBy: 'Jacob and Nathan',
        bookedAt: '2026-07-19T19:11:00',
        amount: 360,
        site: 'Wynyard - Siskin Park',
        plot: '156',
      },
      {
        kind: 'labour',
        title: 'JH - Timber Frame Kit',
        ref: 'LAB-WOME-8',
        status: 'Booked',
        bookedBy: 'Joe Hamlett Gang',
        bookedAt: '2026-07-19T18:42:00',
        amount: 2135,
        site: 'Womersley Road - TF',
        plot: '119',
      },
      {
        kind: 'variation',
        title: 'Fixing holes in ext panel',
        ref: 'VAR-WITH-1',
        status: 'Booked',
        bookedBy: 'Jacob and Nathan',
        bookedAt: '2026-07-19T17:25:00',
        amount: 30,
        site: 'Withernsea - TF',
        plot: '008',
      },
      {
        kind: 'labour',
        title: 'JM-NF - Timber Frame Kit',
        ref: 'LAB-WYNY-2',
        status: 'Booked',
        bookedBy: 'Jacob Moore',
        bookedAt: '2026-07-19T17:22:00',
        amount: 637.5,
        fullAmount: 1769,
        site: 'Wynyard - Siskin Park',
        plot: '156',
      },
      {
        kind: 'variation',
        title: 'Correcting roof plots 156/157',
        ref: 'VAR-WYNY-1',
        status: 'Booked',
        bookedBy: 'Jacob and Nathan',
        bookedAt: '2026-07-19T17:21:00',
        amount: 720,
        site: 'Wynyard - Siskin Park',
        plot: '157',
      },
      {
        kind: 'labour',
        title: 'ODB - Timberframe Kit',
        ref: 'LAB-FW11-3',
        status: 'Booked',
        bookedBy: 'Matthew McGregor',
        bookedAt: '2026-07-19T11:07:00',
        amount: 2278.5,
        fullAmount: 2533,
        site: 'Finchwood 11 - 12',
        plot: '189',
      },
      {
        kind: 'labour',
        title: 'F&S + Fire Baskets',
        ref: 'LAB-WIJO-2',
        status: 'Approved',
        bookedBy: 'Harriet Robinson',
        bookedAt: '2026-07-19T10:54:00',
        amount: 275,
        approvedBy: 'Natalia Kujawa',
        approvedAt: '2026-07-19T16:40:00',
        site: 'Withernsea',
        plot: '20',
      },
      {
        kind: 'labour',
        title: 'KJ - TJ - Timber Frame Kit',
        ref: 'LAB-KIDD-129',
        status: 'Booked',
        bookedBy: 'Kieron and Travis',
        bookedAt: '2026-07-18T14:19:00',
        amount: 1769,
        fullAmount: 1890,
        site: 'Kiddlington - TF',
        plot: '144',
      },
    ],
  },
];

export function weekTotal(week: ActivityWeek): number {
  return week.rows
    .filter(r => r.status !== 'Cancelled')
    .reduce((sum, r) => sum + r.amount, 0);
}

export function potSiteTotal(site: PotSite): number {
  return site.plots
    .flatMap(p => p.rows)
    .filter(r => r.status !== 'Cancelled' && r.status !== 'Rejected')
    .reduce((sum, r) => sum + r.amount, 0);
}
